import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { ChevronDown, ChevronUp, Download, Eye, FileText } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { CardSkeleton } from '../common/States';
import { api, documentCenterAPI } from '../../services/api';
import { getErrorMessage } from '../../lib/utils';

const fmt = (v) => (v ? new Date(v).toLocaleDateString([], { day: '2-digit', month: 'short', year: 'numeric' }) : '');

/** A session-protected file as an object URL. Fetched through the API client:
 *  a plain link to /api/... on the app's own host gets the app, not the file. */
export const fileUrl = async (url) => {
  const { data, headers } = await api.get(url, { responseType: 'blob' });
  return URL.createObjectURL(new Blob([data], { type: headers?.['content-type'] || 'application/pdf' }));
};

/** Opens a session-protected file in a new tab: PDFs we generate come back as blobs. */
export const openFile = async (url) => {
  try {
    const href = await fileUrl(url);
    window.open(href, '_blank');
    setTimeout(() => URL.revokeObjectURL(href), 60000);
  } catch (e) { toast.error(getErrorMessage(e, 'Could not open the file')); }
};

/** Saves a session-protected file under its own name. */
export const downloadFile = async (url, name) => {
  try {
    const href = await fileUrl(url);
    const a = document.createElement('a');
    a.href = href;
    a.download = name || 'document';
    a.click();
    setTimeout(() => URL.revokeObjectURL(href), 60000);
  } catch (e) { toast.error(getErrorMessage(e, 'Could not download the file')); }
};

/** Preview and Download for one file. */
export const FileActions = ({ url, name, title }) => (
  <div className="flex shrink-0 gap-1">
    <Button size="sm" variant="outline" className="h-8 rounded-full" onClick={() => openFile(url)} aria-label={`Preview ${title || name}`}>
      <Eye className="h-3.5 w-3.5 mr-1" />Preview
    </Button>
    <Button size="sm" variant="outline" className="h-8 rounded-full" onClick={() => downloadFile(url, name)} aria-label={`Download ${title || name}`}>
      <Download className="h-3.5 w-3.5 mr-1" />Download
    </Button>
  </div>
);

const Row = ({ title, sub, onOpen, actions }) => (
  <div className="flex items-center justify-between gap-3 rounded-md border border-border px-3 py-2 text-sm">
    <div className="min-w-0"><p className="font-medium truncate">{title}</p>{sub && <p className="text-xs text-muted-foreground">{sub}</p>}</div>
    {actions || <Button size="icon" variant="ghost" onClick={onOpen} aria-label={`Open ${title}`}><Download className="h-4 w-4" /></Button>}
  </div>
);

const Empty = () => <p className="text-sm text-muted-foreground py-2">Nothing here yet.</p>;

/**
 * Everything that is mine, in one place: letters and uploads, payslips by
 * financial year, Form 16, and the company's policies and forms.
 */
const DocumentCenter = () => {
  const [sections, setSections] = useState(null);
  const [open, setOpen] = useState({ my_documents: true });
  const [letter, setLetter] = useState(null);

  useEffect(() => {
    documentCenterAPI.get().then((r) => setSections(r.data.sections)).catch((e) => {
      setSections([]);
      toast.error(getErrorMessage(e, 'Could not load your documents'));
    });
  }, []);

  const openItem = async (item) => {
    if (item.kind === 'letter') {
      try { const { data } = await api.get(item.url); setLetter(data); } catch (e) { toast.error(getErrorMessage(e, 'Not ready yet')); }
      return;
    }
    openFile(item.url);
  };

  const body = (s) => {
    if (s.key === 'payslips') {
      return s.years.length === 0 ? <Empty /> : s.years.map((y) => (
        <div key={y.financial_year} className="space-y-1">
          <p className="text-xs font-semibold text-muted-foreground">{y.financial_year}</p>
          {y.items.map((i) => <Row key={i.id} title={i.title} onOpen={() => openFile(i.url)} />)}
        </div>
      ));
    }
    if (s.key === 'policies') {
      return s.folders.length === 0 ? <Empty /> : s.folders.map((f) => (
        <div key={f.name} className="space-y-1">
          <p className="text-xs font-semibold text-muted-foreground">{f.name}</p>
          {f.items.map((i) => <Row key={i.id} title={i.title} sub={`Last updated ${fmt(i.date)}`}
            actions={<FileActions url={i.url} name={i.file_name} title={i.title} />} />)}
        </div>
      ));
    }
    return s.items.length === 0 ? <Empty /> : s.items.map((i) => (
      <Row key={`${i.kind || s.key}-${i.id || i.financial_year}`} title={i.title}
        sub={i.date ? fmt(i.date) : undefined} onOpen={() => openItem(i)}
        actions={i.kind === 'library' ? <FileActions url={i.url} name={i.file_name} title={i.title} /> : null} />
    ));
  };

  if (sections === null) return <CardSkeleton />;
  return (
    <div className="space-y-4 animate-fade-in" data-testid="document-center">
      <div><h2 className="text-lg font-semibold">Document Center</h2><p className="text-sm text-muted-foreground">Your letters, payslips, Form 16, and the company's policies and forms.</p></div>
      {sections.map((s) => (
        <Card key={s.key}>
          <CardHeader className="cursor-pointer" onClick={() => setOpen((o) => ({ ...o, [s.key]: !o[s.key] }))}>
            <CardTitle className="flex items-center justify-between text-base">
              <span className="flex items-center gap-2"><FileText className="h-4 w-4" />{s.title}</span>
              {open[s.key] ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </CardTitle>
          </CardHeader>
          {open[s.key] && <CardContent className="space-y-2">{body(s)}</CardContent>}
        </Card>
      ))}
      <Dialog open={!!letter} onOpenChange={(o) => { if (!o) setLetter(null); }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>{letter?.title}</DialogTitle><DialogDescription>{letter?.signed_by ? `Signed by ${letter.signed_by}` : 'Generated'}</DialogDescription></DialogHeader>
          <pre className="whitespace-pre-wrap text-sm font-sans max-h-[60vh] overflow-y-auto">{letter?.content}</pre>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DocumentCenter;
